import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Bug, Lightbulb, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type FeedbackType = "bug" | "change_request" | "idea";
type Priority = "low" | "medium" | "high" | "critical";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const location = useLocation();
  const { userProfile } = useAuth();
  const [feedbackType, setFeedbackType] = useState<FeedbackType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<Priority>("medium");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setFeedbackType("bug");
    setTitle("");
    setDescription("");
    setPriority("medium");
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.from("user_feedback" as any).insert({
        user_id: userProfile?.id ?? null,
        user_email: userProfile?.email ?? null,
        user_name: userProfile?.full_name ?? userProfile?.first_name ?? null,
        feedback_type: feedbackType,
        title: title.trim().slice(0, 200),
        description: description.trim().slice(0, 2000) || null,
        current_route: location.pathname,
        priority: feedbackType === "bug" ? priority : "medium",
      } as any);

      if (error) throw error;

      toast.success("Feedback submitted — thank you!");
      reset();
      onOpenChange(false);
    } catch (err: any) {
      console.error("Feedback submit error:", err);
      toast.error("Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  const typeConfig = {
    bug: { label: "Bug", icon: Bug, color: "text-destructive" },
    change_request: { label: "Change", icon: RefreshCw, color: "text-primary" },
    idea: { label: "Idea", icon: Lightbulb, color: "text-yellow-500" },
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share Feedback</DialogTitle>
          <DialogDescription>
            Report a bug, request a change, or share an idea.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Type selector */}
          <div className="space-y-2">
            <Label>Type</Label>
            <ToggleGroup
              type="single"
              value={feedbackType}
              onValueChange={(v) => v && setFeedbackType(v as FeedbackType)}
              className="justify-start"
            >
              {(Object.entries(typeConfig) as [FeedbackType, typeof typeConfig.bug][]).map(
                ([key, cfg]) => (
                  <ToggleGroupItem
                    key={key}
                    value={key}
                    className="gap-1.5 data-[state=on]:bg-accent"
                  >
                    <cfg.icon className={`h-4 w-4 ${cfg.color}`} />
                    {cfg.label}
                  </ToggleGroupItem>
                )
              )}
            </ToggleGroup>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="fb-title">Title *</Label>
            <Input
              id="fb-title"
              placeholder="Brief summary…"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="fb-desc">Description</Label>
            <Textarea
              id="fb-desc"
              placeholder="Details, steps to reproduce, or context…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={4}
            />
          </div>

          {/* Priority — only for bugs */}
          {feedbackType === "bug" && (
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as Priority)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Route context chip */}
          <p className="text-xs text-muted-foreground">
            Page: <code className="bg-muted px-1 rounded">{location.pathname}</code>
          </p>

          <Button
            onClick={handleSubmit}
            disabled={submitting || !title.trim()}
            className="w-full gap-2"
          >
            <Send className="h-4 w-4" />
            {submitting ? "Submitting…" : "Submit Feedback"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
